import { Component, type ReactNode } from 'react';

// Shared client-side production telemetry for CCAANZ portals (maintenance regime
// Phase 4, House O). Captures render errors (ErrorBoundary), window errors,
// unhandled rejections and API failures, and POSTs a compact, PII-redacted event
// to the portal's /api/telemetry. The worker stamps the portal + deployed SHA on
// ingest, so the client never sends those. Telemetry must NEVER throw or surface
// to the user — every path is guarded and best-effort.

interface TelemetryConfig {
  /** Which portal this is — tags the event (validated server-side). */
  portal?: string;
  /** Ingest endpoint. Default '/api/telemetry' (same-origin / dev-proxied). */
  endpoint?: string;
  /** Hard cap on automatic events per page session (stops a render-loop flooding). */
  maxPerSession?: number;
  /** A person's own allowance of "Report a problem" reports per page session. Kept
   *  apart from the cap above, so an error storm cannot use it up (cca-4hiyz). */
  maxReportsPerSession?: number;
}

let CONFIG: Required<TelemetryConfig> = {
  portal: '',
  endpoint: '/api/telemetry',
  maxPerSession: 20,
  maxReportsPerSession: 5,
};
/** Automatic events dispatched this session. */
let sent = 0;
/** A person's reports dispatched this session — counted apart from `sent`. */
let reports = 0;
const seen = new Set<string>();

/** The kind the "Report a problem" panel sends: a person's own words. */
const USER_REPORT = 'user_report';

/** Strip obvious PII before anything leaves the browser (the server clamps too). */
function redact(s: string): string {
  return s
    .replace(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, '[email]')
    .replace(/\b\d{6,}\b/g, '#')
    .trim()
    .slice(0, 500);
}

/**
 * The session's dedupe key, built from the REDACTED message, never the raw one.
 * A person's report keeps all of its words: "page 1 broke" and "page 2 broke" are
 * two reports. An automatic event folds its digits and keeps 120 characters, so
 * one fault firing with changing ids or counts is one event.
 */
function dedupeKey(kind: string, route: string, msg: string): string {
  if (kind === USER_REPORT) return `${kind}|${route}|${msg}`;
  return `${kind}|${route}|${msg.replace(/\d+/g, '#').slice(0, 120)}`;
}

/**
 * What became of one event: `'sent'`, or the gate that stopped it.
 *
 * - `'sent'` — it cleared this client's gates and its POST was dispatched. It
 *   cannot mean the server stored it: the POST is fire-and-forget (a network
 *   failure is swallowed below), and the ingest doors answer 200 even when they
 *   drop an event.
 * - `'repeat'` — the same kind and words were already sent from this route this
 *   session (for an automatic event, the same words once digits are folded).
 * - `'cap'` — this session's allowance is used: by default 20 automatic events,
 *   or a person's 5 reports, each counted apart from the other.
 * - `'empty'` — nothing left to send once trimmed.
 * - `'failed'` — the dispatch threw.
 */
export type TelemetryOutcome = 'sent' | 'repeat' | 'cap' | 'empty' | 'failed';

/**
 * Queue one event, and say what became of it ({@link TelemetryOutcome}). Deduped
 * within the session and hard-capped; never throws. The "Report a problem" panel
 * reads the answer, so it says "sent" only for a report that left, and otherwise
 * names the gate that stopped it (cca-4hiyz).
 */
export function queueTelemetry(kind: string, message: string, route?: string): TelemetryOutcome {
  try {
    const r = route ?? (typeof location !== 'undefined' ? location.pathname : '');
    const msg = redact(String(message ?? '').trim());
    if (!msg) return 'empty';
    const key = dedupeKey(kind, r, msg);
    if (seen.has(key)) return 'repeat';
    const isReport = kind === USER_REPORT;
    if (isReport ? reports >= CONFIG.maxReportsPerSession : sent >= CONFIG.maxPerSession) {
      return 'cap';
    }
    // Counted BEFORE the dispatch, so anything the dispatch itself sets off meets
    // this event as a repeat rather than looping.
    seen.add(key);
    if (isReport) reports++;
    else sent++;
    try {
      void fetch(CONFIG.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        keepalive: true, // best-effort during unload / crash
        body: JSON.stringify({ portal: CONFIG.portal, kind, message: msg, route: r }),
      }).catch(() => {});
    } catch {
      // A person's report whose dispatch threw was NOT sent: it is un-counted, so
      // pressing Send again is a fresh try, never told it was "already sent". An
      // automatic event stays counted, as it always has.
      if (isReport) {
        seen.delete(key);
        reports--;
      }
      return 'failed';
    }
    return 'sent';
  } catch {
    /* telemetry must never throw */
    return 'failed';
  }
}

/**
 * Report one event: {@link queueTelemetry} with the answer dropped, for a caller
 * that only reports. It stays `void` on purpose — a caller typed to return
 * `void | Promise<void>` (Sexton's `onReport`, which forms-web hands this) cannot
 * be given a string.
 */
export function reportTelemetry(kind: string, message: string, route?: string): void {
  queueTelemetry(kind, message, route);
}

/** Wire global handlers. Call once at app start. */
export function initTelemetry(config: TelemetryConfig = {}): void {
  CONFIG = { ...CONFIG, ...config };
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => {
    reportTelemetry('client_error', e.message || 'window error');
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason as { message?: string } | undefined;
    reportTelemetry('unhandled_rejection', reason?.message ?? String(e.reason));
  });
}

/** Wrap an app so a render crash is captured and a calm fallback is shown. */
export class TelemetryErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean }
> {
  override state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error: Error) {
    reportTelemetry('client_error', `Render: ${error.message}`);
  }

  override render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif', color: '#374151' }}>
            <h2 style={{ color: '#5c4f33' }}>Something went wrong</h2>
            <p>
              The error has been logged. Please reload the page — if it persists, let the registrar
              know.
            </p>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
